import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';

const normalizePlaceholderKey = (placeholder) => {
  return placeholder
    .trim()
    .replace(/[_\s]+/g, '_')
    .toUpperCase();
};

/**
 * Load and parse a .docx template
 * @param {string} templateUrl - URL or path to the .docx template
 * @returns {Promise<Object>} Object containing doc instance and placeholders
 */
export const loadDocxTemplate = async (templateUrl) => {
  try {
    let arrayBuffer;

    // Check if the URL is a base64 data URI
    if (templateUrl.startsWith('data:')) {
      const base64String = templateUrl.split(',')[1];
      const binaryString = window.atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      arrayBuffer = bytes.buffer;
    } else {
      // Fetch the document
      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      arrayBuffer = await response.arrayBuffer();
    }

    // Load the docx file as binary content
    const zip = new PizZip(arrayBuffer);

    // Create docxtemplater instance
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '$[', end: ']' }, // Match $[PLACEHOLDER] format
    });

    // Extract all placeholders
    const { placeholders, placeholderGroups } = extractPlaceholders(doc);

    return {
      doc,
      zip,
      arrayBuffer,
      placeholders,
      placeholderGroups,
    };
  } catch (error) {
    console.error('Error loading document template:', error);
    throw new Error(`Failed to load document: ${error.message}`);
  }
};

/**
 * Extract placeholder names from the document
 * @param {Docxtemplater} doc - The docxtemplater instance
 * @returns {Array<string>} Array of unique placeholder names
 */
const extractPlaceholders = (doc) => {
  try {
    // Get the full text from the document
    const fullText = doc.getFullText();

    // Match all placeholders in format $[PLACEHOLDER NAME] or $[PLACEHOLDER_NAME]
    // Allowing letters, numbers, underscores, and spaces
    const regex = /\$\[([a-zA-Z0-9_\s]+)\]/g;
    const groupMap = new Map();
    let match;

    while ((match = regex.exec(fullText)) !== null) {
      const placeholderName = match[1].trim();
      if (!placeholderName) continue;

      const key = normalizePlaceholderKey(placeholderName);
      if (!groupMap.has(key)) {
        groupMap.set(key, new Set());
      }
      groupMap.get(key).add(placeholderName);
    }

    const placeholders = Array.from(groupMap.keys());
    const placeholderGroups = placeholders.map((key) => ({
      key,
      variants: Array.from(groupMap.get(key) || []),
    }));

    return { placeholders, placeholderGroups };
  } catch (error) {
    console.error('Error extracting placeholders:', error);
    return { placeholders: [], placeholderGroups: [] };
  }
};

/**
 * Fill the document with provided data
 * @param {Docxtemplater} doc - The docxtemplater instance
 * @param {Object} data - Key-value pairs for placeholder replacement
 * @param {Array<Object>} [placeholderGroups] - Optional mapping of canonical keys to all placeholder variants
 * @returns {ArrayBuffer} The filled document as ArrayBuffer
 */
export const fillDocument = (doc, data, placeholderGroups) => {
  try {
    const expandedData = { ...data };

    // If placeholderGroups were provided, apply the canonical field value to all placeholder variants
    if (Array.isArray(placeholderGroups)) {
      placeholderGroups.forEach((group) => {
        const value = data?.[group?.key];
        if (!Array.isArray(group?.variants)) return;
        group.variants.forEach((variant) => {
          if (variant) {
            expandedData[variant] = value;
          }
        });
      });
    }

    // Set the template data
    doc.setData(expandedData);

    // Render the document (replace all placeholders)
    doc.render();

    // Generate the document as a blob
    const blob = doc.getZip().generate({
      type: 'arraybuffer',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    return blob;
  } catch (error) {
    console.error('Error filling document:', error);
    throw new Error(`Failed to fill document: ${error.message}`);
  }
};
